import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { setDrawerAlert, setStage } from 'slices/drawer'
import { STATICS } from 'statics'
import {
  CONTACT_OPTIONS,
  CONTACT_TYPE,
  DEFAULT_ERROR_MESSAGE,
  RESEND_TOKEN_TIME,
  TOKEN_CONFIRMATION_ERROR_MESSAGE,
  TOKEN_LENGTH,
} from 'app-constants'
import { contactService, tokenService } from 'services'
import { formatPhoneToDB, getMaskedContact, sendWhatsappMessage } from 'utils'
import { Input } from 'components/input'
import { Button } from 'components/button'
import { CountdownTime } from 'components/countdown-time'
import './style.scss'

export function ContactConfirmation({ data, isMain = false, isPhone, nextStage = '' }) {
  const [userMainContacts, setUserMainContacts] = useState({ PHONE: '', MAIL: '' })
  const [token, setToken] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [canSendCode, setCanSendCode] = useState(false)
  const [loading, setLoading] = useState({
    sendCode: false,
    confirmToken: false,
  })
  const { generateToken } = tokenService()
  const { createNewEmail, createNewPhone } = contactService()
  const { document, contacts } = useSelector(({ customer }) => customer.info)
  const { contact: newContact } = data
  const dispatch = useDispatch()

  useEffect(() => {
    function getUserMainContacts() {
      const phone = contacts.find(
        contact => contact.principal && contact.contactType === CONTACT_TYPE.PHONE
      )
      const email = contacts.find(
        contact => contact.principal && contact.contactType === CONTACT_TYPE.EMAIL
      )

      setUserMainContacts({
        PHONE: phone.contact,
        MAIL: email.contact,
      })
    }

    getUserMainContacts()
  }, [contacts])

  useEffect(() => {
    sendConfirmationCode()
  }, [document, userMainContacts.PHONE, userMainContacts.MAIL])

  async function sendConfirmationCode() {
    try {
      setLoading({ ...loading, sendCode: true })
      if (userMainContacts.PHONE)
        await generateToken(document, userMainContacts.PHONE, CONTACT_OPTIONS.SMS)
      if (userMainContacts.MAIL)
        await generateToken(document, userMainContacts.MAIL, CONTACT_OPTIONS.EMAIL)
    } catch (error) {
      console.error(error)
      const message = error.response?.data?.message || DEFAULT_ERROR_MESSAGE
      dispatch(setDrawerAlert({ severity: 'error', title: message }))
    } finally {
      setLoading({ ...loading, sendCode: false })
    }
  }

  function handleInputChange(event) {
    const { value } = event.target

    setToken(value)
  }

  function handleAtendentClick() {
    sendWhatsappMessage({})
  }

  async function handleResendCodeClick() {
    await sendConfirmationCode()
    setCanSendCode(false)
  }

  async function handleConfirmToken() {
    try {
      setLoading({ ...loading, confirmToken: true })

      const body = {
        isMain,
        token,
      }
      if (isPhone) {
        body.phone = formatPhoneToDB(newContact)
        await createNewPhone(body)
      } else {
        body.email = newContact
        await createNewEmail(body)
      }

      dispatch(setStage(nextStage))
    } catch (error) {
      console.error(error)
      const message = error.response?.data?.message || TOKEN_CONFIRMATION_ERROR_MESSAGE
      setErrorMessage(message)
      setLoading({ ...loading, confirmToken: false })
    }
  }

  function handleExpiredCode() {
    setCanSendCode(true)
  }

  function renderCountdown() {
    if (canSendCode) return null

    return (
      <span className='text'>
        restam{' '}
        <CountdownTime
          onFinishCounter={handleExpiredCode}
          time={RESEND_TOKEN_TIME}
          canSendCode={canSendCode}
        />{' '}
        segundos
      </span>
    )
  }

  return (
    <div className='contact-confirmation-stage'>
      <span className='title'>Validaremos seu contato cadastrado</span>
      <span className='description'>
        Para proteger sua conta, enviamos um código de verificação para seus contatos cadastrados:
      </span>
      <div className='container-contacts'>
        <span className='title'>Telefone</span>
        <span className='text'>{getMaskedContact(userMainContacts.PHONE)}</span>
        <span className='title'>E-mail</span>
        <span className='text'>{getMaskedContact(userMainContacts.MAIL)}</span>
      </div>
      <div className='container-token-confirmation'>
        <span className='title'>
          Digite o código recebido para continuar a alteração dos seus dados cadastrais.
        </span>
        <Input
          onChange={handleInputChange}
          className='input'
          name='code'
          placeholder='Código recebido'
          maxLength={TOKEN_LENGTH}
          hasError={!!errorMessage}
          errorMessage={errorMessage}
        />
        <div className='container-token-resend'>
          <Button
            className='button'
            color='primary'
            disabled={!canSendCode}
            onClick={handleResendCodeClick}
            isLoading={loading.sendCode}
          >
            Não recebi o código
          </Button>
          {renderCountdown()}
        </div>
        <Button
          isLoading={loading.confirmToken}
          className='confirm-button'
          color='primary'
          onClick={handleConfirmToken}
        >
          Continuar
        </Button>
      </div>
      <div className='container-unknow'>
        <span className='title'>Não reconhece esses contatos?</span>
        <span className='description'>
          Se você não reconhece os contatos acima ou não tem mais acesso a eles, entre em contato
          pelo nosso whatsapp (22) 2041-0353
        </span>
        <button type='button' onClick={handleAtendentClick} className='button'>
          Falar com atendimento <STATICS.ARROW_NEXT />
        </button>
      </div>
    </div>
  )
}
